import { utils } from "@/utils/utils";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

@Component({
  name: "b-form-input-datepicker",
  components: { DatePicker },
})
export default class FormInputDatepickerComponent extends Vue {
  @Prop() public formType!: string;
  @Prop() public editForm!: boolean;
  @Prop() public keyName!: string;
  @Prop() public value!: string;
  @Prop() public payloadData: any;
  @Prop() public isPreviewForm!: boolean;
  @Prop({ default: null }) public minAge!: number | null;
  @Prop({ default: true }) public disableFuture!: boolean;
  /*
   * Month-and-year only, for a date of birth. The day is neither asked for nor
   * sent: the calendar stops at the month panel and the value stays MM-YYYY.
   */
  @Prop({ default: false }) public monthOnly!: boolean;

  public isDisable = false;
  public displayDate: string | null = null;
  public validationError = "";
  private hasInputError = false;
  private rawInput = "";
  private skipNextWatch = false;

  get pickerType(): string {
    return this.monthOnly ? "month" : "date";
  }

  get displayFormat(): string {
    return this.monthOnly ? "MM-YYYY" : "MM-DD-YYYY";
  }

  get inputClass(): string {
    return (
      "date-input form-control" + (this.validationError ? " is-invalid" : "")
    );
  }

  public created() {
    this.isDisable = utils.disableField(this.editForm, this.formType);
    this.skipNextWatch = true;
    this.displayDate = this.toDisplayDate(this.value);
    /*
     * A date of birth saved before MM-YYYY was the rule is shown as MM-YYYY
     * above; write it back in that shape too, so re-saving a form the
     * applicant never touched stops carrying the old format forward.
     */
    if (this.monthOnly && this.displayDate && this.displayDate !== this.value) {
      this.$emit("input", this.displayDate);
    }
  }

  public mounted() {
    const pickerEl = (this.$refs.datePicker as any)?.$el;
    if (pickerEl) {
      pickerEl.addEventListener(
        "input",
        (e: Event) => {
          this.rawInput = (e.target as HTMLInputElement).value;
        },
        true // capture phase — fires before datepicker's own listener
      );
    }
  }

  public onCalendarOpen() {
    this.rawInput = "";
  }

  public onPickerBlur() {
    if (this.validationError && this.rawInput) {
      const typed = this.rawInput;
      this.$nextTick(() => {
        const picker = this.$refs.datePicker as any;
        if (picker) picker.userInput = typed;
      });
    }
  }

  /*
   * The newest date that still satisfies minAge, or null when no minimum age
   * applies. A date of birth on this day means the applicant turns minAge
   * today, so it is allowed; anything after it is not.
   */
  // True when the only thing wrong with the value is the age rule.
  private isAgeError = false;

  private get latestAllowedDate(): Date | null {
    if (!this.minAge) return null;
    const latest = new Date();
    latest.setHours(0, 0, 0, 0);
    latest.setFullYear(latest.getFullYear() - this.minAge);
    return latest;
  }

  public disableFutureDates(date: Date): boolean {
    if (!this.disableFuture) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
  }

  public disabledCalendarChanger(date: Date, type: string): boolean {
    if (!this.disableFuture) return false;
    if (type === "year") {
      return date.getFullYear() > new Date().getFullYear();
    }
    return false;
  }

  public onDateInput(value: string | null) {
    if (!value) return;
    if (this.rawInput && this.rawInput !== value) {
      const rawError = this.validateDate(this.rawInput);
      this.validationError =
        rawError ||
        `Please enter a valid date in ${this.displayFormat} format.`;
      const typed = this.rawInput;
      this.$nextTick(() => {
        const picker = this.$refs.datePicker as any;
        if (picker) picker.userInput = typed;
      });
      return;
    }
    this.rawInput = "";
    this.validationError = this.validateDate(value);
    // A date that is real but too young is still the applicant's answer: keep
    // it, so the form sees it and can say so, rather than silently dropping it
    // and leaving an empty field with an error beside it.
    if (!this.validationError || this.isAgeError) {
      this.skipNextWatch = true;
      this.$emit("input", this.toISODate(value));
    }
  }

  public onInputError(rawValue: string) {
    if (rawValue) {
      this.rawInput = rawValue;
      this.hasInputError = true;
      this.validationError = this.validateDate(rawValue);
      this.$nextTick(() => {
        const picker = this.$refs.datePicker as any;
        if (picker) picker.userInput = rawValue;
      });
    }
  }

  @Watch("displayDate")
  onDisplayDateChange() {
    if (this.skipNextWatch) {
      this.skipNextWatch = false;
      return;
    }
    if (this.hasInputError) {
      this.hasInputError = false;
    }
  }

  @Watch("value")
  onValueChange(newVal: string) {
    this.rawInput = "";
    this.validationError = "";
    this.skipNextWatch = true;
    this.displayDate = this.toDisplayDate(newVal);
  }

  private validateDate(val: string | null): string {
    this.isAgeError = false;
    if (!val) return "";
    if (this.monthOnly) {
      return this.validateMonthYear(val);
    }
    const parts = val.split("-");
    if (
      parts.length !== 3 ||
      parts[0].length !== 2 ||
      parts[1].length !== 2 ||
      parts[2].length !== 4
    ) {
      return "Please enter a valid date in MM-DD-YYYY format.";
    }
    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    const date = new Date(year, month - 1, day);
    if (
      date.getMonth() !== month - 1 ||
      date.getDate() !== day ||
      date.getFullYear() !== year
    ) {
      return "Please enter a valid date.";
    }
    if (this.disableFuture && date > new Date()) {
      return "Future dates are not allowed.";
    }
    const latest = this.latestAllowedDate;
    if (latest && date > latest) {
      this.isAgeError = true;
      return `You must be at least ${this.minAge} years old.`;
    }
    return "";
  }

  /*
   * MM-YYYY, checked the same way as a full date but without a day. The age
   * rule is applied to the first of the month - see utils.monthYearToDate.
   */
  private validateMonthYear(val: string): string {
    const parts = val.split("-");
    if (parts.length !== 2 || parts[0].length !== 2 || parts[1].length !== 4) {
      return "Please enter a valid date in MM-YYYY format.";
    }
    const date = utils.monthYearToDate(val);
    if (!date) {
      return "Please enter a valid date in MM-YYYY format.";
    }
    if (this.disableFuture && date > new Date()) {
      return "Future dates are not allowed.";
    }
    const latest = this.latestAllowedDate;
    if (latest && date > latest) {
      this.isAgeError = true;
      return `You must be at least ${this.minAge} years old.`;
    }
    return "";
  }

  private toDisplayDate(val: string): string | null {
    if (!val) return null;
    // A date of birth stored in any older shape still shows as MM-YYYY.
    if (this.monthOnly) {
      return utils.toMonthYear(val) || null;
    }
    const parts = val.split("-");
    // Convert YYYY-MM-DD (ISO from API) → MM-DD-YYYY (display)
    if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[1]}-${parts[2]}-${parts[0]}`;
    }
    return val;
  }

  private toISODate(val: string | null): string {
    if (!val) return "";
    // Month and year are all that is collected, so that is all that is sent.
    if (this.monthOnly) {
      return utils.toMonthYear(val);
    }
    const parts = val.split("-");
    // Convert MM-DD-YYYY (display) → YYYY-MM-DD (ISO for API/payload)
    if (parts.length === 3 && parts[2].length === 4) {
      return `${parts[2]}-${parts[0]}-${parts[1]}`;
    }
    return val;
  }
}
